import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Map, FileText, ClipboardList, Settings,
  Bell, Search, Menu, LogOut, Layers, GraduationCap, CalendarCheck, CalendarDays, ScrollText,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { api, roleLabel, initials, avatarColor } from "@/lib/api";

const ALLR = ["admin", "rcsm", "cnat", "hr", "clinical_training_manager"];
const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ALLR },
  { to: "/people", label: "People", icon: Users, roles: ALLR },
  { to: "/work", label: "Work", icon: ClipboardList, roles: ALLR },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, roles: ALLR },
  { to: "/training-matrix", label: "Training Matrix", icon: GraduationCap, roles: ALLR },
  { to: "/reviews", label: "Reviews", icon: CalendarCheck, roles: ALLR },
  { to: "/notifications", label: "Notifications", icon: Bell, roles: ALLR },
  { to: "/templates", label: "Templates", icon: FileText, roles: ALLR },
  { to: "/regions", label: "Regions", icon: Map, roles: ["admin"] },
  { to: "/users", label: "Users & Roles", icon: Layers, roles: ["admin", "hr"] },
  { to: "/gdpr-register", label: "GDPR Register", icon: ScrollText, roles: ["admin"] },
  { to: "/administration", label: "Administration", icon: Settings, roles: ["admin"] },
];

function NavItems({ role, onNavigate, unread }) {
  return (
    <nav className="flex flex-col gap-1 px-3" data-testid="sidebar-nav">
      {NAV.filter((n) => n.roles.includes(role)).map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-white/10 text-white border-l-2 border-orange-500 pl-[10px]"
                  : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {item.label}
            {item.to === "/notifications" && unread > 0 && (
              <span data-testid="nav-unread-badge" className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-orange-500 px-1.5 text-[11px] font-bold text-white">
                {unread}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-3 px-5 py-5">
      <img src="/logo.png" alt="ESP26" className="h-10 w-10 rounded-xl object-cover" />
      <div className="leading-tight">
        <p className="font-heading text-lg font-extrabold tracking-tight text-white">ESP26</p>
        <p className="text-[11px] text-slate-400">People. Skills. Progress.</p>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, impersonate, stopImpersonation, impersonating } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    if (user?.is_super) api.get("/users").then((r) => setAllUsers(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
    load();
    const handler = () => load();
    window.addEventListener("esp26-notifications-updated", handler);
    const id = setInterval(load, 60000);
    return () => { window.removeEventListener("esp26-notifications-updated", handler); clearInterval(id); };
  }, [user]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-[#0F172A] lg:flex">
        <Brand />
        <div className="mt-2 flex-1 overflow-y-auto esp-scroll">
          <NavItems role={user.role} unread={unread} />
        </div>
        <div className="border-t border-white/10 p-4">
          {user.is_super && !impersonating && (
            <div className="mb-3">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-orange-400">View as (Super Admin)</p>
              <select
                data-testid="view-as-select"
                onChange={(e) => e.target.value && impersonate(e.target.value)}
                defaultValue=""
                className="w-full rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-xs text-white"
              >
                <option value="" className="text-slate-900">Choose a user…</option>
                {allUsers.filter((u) => !u.is_super).map((u) => (
                  <option key={u.id} value={u.id} className="text-slate-900">{u.name} — {roleLabel(u.role)}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold ${avatarColor(user.name)}`}>
              {initials(user.name)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-white">{user.name}</p>
              <p className="truncate text-[11px] text-slate-400">{user.is_super && !impersonating ? "Super Administrator" : roleLabel(user.role)}</p>
            </div>
            <button onClick={logout} data-testid="logout-button" className="text-slate-400 hover:text-orange-400">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <header
        data-testid="app-header"
        className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur-md lg:hidden"
      >
        <div className="flex items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-trigger" className="rounded-lg p-2 text-slate-700 hover:bg-slate-100">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-0 bg-[#0F172A] p-0">
              <Brand />
              <div className="mt-2">
                <NavItems role={user.role} unread={unread} onNavigate={() => setMobileOpen(false)} />
              </div>
              <div className="mt-4 border-t border-white/10 p-4">
                <button onClick={logout} className="flex items-center gap-2 text-sm text-slate-300 hover:text-orange-400">
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            </SheetContent>
          </Sheet>
          <img src="/logo.png" alt="ESP26" className="h-8 w-8 rounded-lg" />
          <span className="font-heading text-base font-extrabold">ESP26</span>
        </div>
        <button onClick={() => navigate("/notifications")} data-testid="mobile-bell" className="relative rounded-lg p-2 text-slate-700 hover:bg-slate-100">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white">{unread}</span>}
        </button>
      </header>

      {/* Main content */}
      <main className="lg:pl-64">
        {impersonating && (
          <div data-testid="impersonation-banner" className="flex items-center justify-between gap-2 bg-orange-500 px-4 py-2 text-sm font-medium text-white">
            <span>Viewing as {user.name} ({roleLabel(user.role)}) — this is what they see when they log in.</span>
            <button data-testid="exit-impersonation" onClick={stopImpersonation} className="rounded-md bg-white/20 px-3 py-1 text-xs font-semibold hover:bg-white/30">
              Exit view
            </button>
          </div>
        )}
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</div>
      </main>
    </div>
  );
}
